<template>
	<div>
		<div class="row">
			<div class="col-12 col-md-3">
				<h2>{{ $t('response-value-group') }}</h2>
			</div>
			<div class="col-12 col-md-9 text-right">
				<button @click="save" class="btn btn-primary">{{ $t('save') }}</button>
			</div>
		</div>

		<div v-if="saved">
			<div class="alert alert-success mt-8">{{ $t('saved') }}</div>
		</div>

		<div v-if="error">
			<div class="alert alert-danger mt-8">{{ error }}</div>
		</div>

		<div class="form-group">
			<label for="group-name">{{ $t('name') }}</label>
			<input id="group-name" class="form-control" v-model="group.name" :placeholder="$t('name')">
		</div>

		<div class="form-group">
			<label for="group-title">{{ $t('title') }}</label>
			<input id="group-title" class="form-control" v-model="group.title" :placeholder="$t('title')">
		</div>

		<hr>

		<label>{{ $t('values') }}</label>

		<div v-for="(value, index) in sortedValues" :key="value.key" class="card mb-3">
			<div class="card-body">
				<div class="row">
					<div class="col-md-2">
						<div class="form-group">
							<label>{{ $t('value') }}</label>
							<input type="number" class="form-control" v-model.number="value.value" :disabled="value.value === -1">
							<div class="form-check mt-1">
								<input
									type="checkbox"
									class="form-check-input"
									:id="'na-' + value.key"
									:checked="value.value === -1"
									@change="toggleValueNA(value, $event.target.checked)"
								>
								<label class="form-check-label" :for="'na-' + value.key">{{ $t('no-value-na') }}</label>
							</div>
						</div>
					</div>
					<div class="col-md-4">
						<div class="form-group">
							<label>{{ $t('caption') }}</label>
							<input class="form-control" v-model="value.caption">
						</div>
					</div>
					<div class="col-md-2">
						<div class="form-group">
							<label>{{ $t('ordering') }}</label>
							<input type="number" class="form-control" v-model.number="value.ordering">
						</div>
					</div>
					<div class="col-md-4 text-right">
						<label class="d-block">{{ $t('colour') }}</label>
						<BtnOnClassPicker :option="value" :okey="value.key"></BtnOnClassPicker>
						<button class="btn btn-outline-danger mt-4 ml-2" @click="removeValue(index)">{{ $t('remove') }}</button>
					</div>
				</div>
				<div class="form-group">
					<label>{{ $t('description') }}</label>
					<vue-editor v-model="value.description"></vue-editor>
				</div>
			</div>
		</div>

		<button class="btn btn-light" @click="addValue">
			<b-icon icon="plus" width="1em" height="1em"></b-icon> {{ $t('add-value') }}
		</button>
	</div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import BtnOnClassPicker from "src/components/FormEditor/BtnOnClassPicker";

export default {
	components: {
		VueEditor,
		BtnOnClassPicker
	},
	data() {
		return {
			responsevaluegroup_id: null,
			saved: false,
			error: null,
			group: {
				name: '',
				title: ''
			},
			values: []
		};
	},
	methods: {
		addValue() {
			this.values.push({
				key: 'value_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
				responsevalue_id: null,
				value: this.values.length,
				caption: '',
				description: null,
				cssclass: '',
				ordering: this.values.length
			});
		},
		removeValue(index) {
			this.values.splice(index, 1);
		},
		// `value` is an int NOT NULL column, so null can't represent "no value" - a saved
		// null is either rejected outright (strict SQL mode) or silently coerced to 0,
		// which is how an N/A option has previously gone missing after being saved.
		// -1 is used as the explicit "no value (N/A)" sentinel instead.
		toggleValueNA(value, checked) {
			this.$set(value, 'value', checked ? -1 : 0);
		},
		save() {
			this.error = null;

			const data = {
				id: this.responsevaluegroup_id,
				name: this.group.name,
				title: this.group.title,
				values: this.values.map(({ responsevalue_id, value, caption, description, cssclass, ordering }) => ({
					responsevalue_id,
					value,
					caption,
					description,
					cssclass,
					ordering
				}))
			};

			this.$cfetch(`${this.$settings.APIBaseURL}/responsevaluegroups/save`, {
				method: "post",
				data
			})
			.then(json => {
				if (json.status === 'SUCCESS') {
					this.responsevaluegroup_id = json.result.id;
					this.saved = true;
					setTimeout(() => {
						this.saved = false;
					}, 5000);
				} else {
					this.error = json.errorMessage || this.$t('unexpected-error-saving');
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
				this.error = this.$t('unexpected-error-saving');
			});
		},
		getItem(id) {
			this.$cfetch(`${this.$settings.APIBaseURL}/responsevaluegroups/getitem/${id}`)
			.then(json => {
				this.$store.commit("hideBlockingSpinner");
				if (json.status === 'SUCCESS') {
					this.responsevaluegroup_id = json.result.id;
					this.group.name = json.result.name;
					this.group.title = json.result.title;
					// The API returns `value`/`ordering` as numeric strings (no int-cast layer
					// server-side, unlike bool fields) - coerce to real numbers so strict
					// comparisons like the N/A checkbox's `value.value === -1` work after reload.
					this.values = (json.result.values || []).map(v => ({
						key: 'value_' + v.responsevalue_id,
						...v,
						value: Number(v.value),
						ordering: Number(v.ordering)
					}));
				} else {
					this.error = json.errorMessage || this.$t('unexpected-error-loading');
				}
			})
			.catch(error => {
				this.$log.error.call(this, error);
				this.$store.commit("hideBlockingSpinner");
				this.error = this.$t('unexpected-error-loading');
			});
		}
	},
	computed: {
		sortedValues() {
			return [...this.values].sort((a, b) => (a.ordering || 0) - (b.ordering || 0));
		}
	},
	mounted() {
		if (this.$route.params.responsevaluegroupid) {
			this.getItem(this.$route.params.responsevaluegroupid);
		} else {
			this.$store.commit("hideBlockingSpinner");
		}
	}
};
</script>
